import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ContractSanction,
  ContractSanctionsParams,
  CounterpartyAccountDebtDynamics,
  CounterpartyAccountDebtDynamicsParams,
  CounterpartyAccountLedger,
  CounterpartyAccountLedgerParams,
  CounterpartyAccrual,
  CounterpartyAccrualDays,
  CounterpartyAccrualDaysParams,
  CounterpartyAccrualsParams,
} from './types';

const MAX_ACCRUAL_ROWS = 1000;

export const counterpartyAccountsApi = createApi({
  reducerPath: 'counterpartyAccountsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/counterparties',
  }),
  tagTypes: [
    'CounterpartyAccountLedgers',
    'CounterpartyAccountDebtDynamics',
    'CounterpartyAccruals',
    'CounterpartyAccrualDays',
    'CounterpartyActsAndInvoices',
    'ContractSanctions',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getCounterpartyAccountLedger: builder.query<
      CounterpartyAccountLedger[],
      CounterpartyAccountLedgerParams
    >({
      query: ({ counterpartyId, ...params }) => ({
        url: `/${counterpartyId}/account/ledger`,
        params,
        paramsSerializer: { indexes: null },
      }),
      providesTags: () => ['CounterpartyAccountLedgers'],
      transformResponse: (response: CounterpartyAccountLedger[]) =>
        response.map(item => ({ ...item, id: item.period })),
    }),
    getCounterpartyAccruals: builder.query<
      CounterpartyAccrual[],
      CounterpartyAccrualsParams,
      FiltersGrid
    >({
      query: ({ counterpartyId, ...params }) => ({
        url: `/${counterpartyId}/account/accruals`,
        params: { ...params, page: 1, pageSize: MAX_ACCRUAL_ROWS },
      }),
      providesTags: () => ['CounterpartyAccruals'],
    }),
    getCounterpartyAccrualDays: builder.query<
      CounterpartyAccrualDays,
      CounterpartyAccrualDaysParams
    >({
      query: ({ counterpartyId, ...params }) => ({
        url: `/${counterpartyId}/account/accruals/days`,
        params,
        paramsSerializer: { indexes: null },
      }),
      providesTags: () => ['CounterpartyAccrualDays'],
    }),
    getCounterpartyAccountDebtDynamics: builder.infiniteQuery<
      CounterpartyAccountDebtDynamics[],
      CounterpartyAccountDebtDynamicsParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/${queryArg?.counterpartyId}/account/debt-dynamics`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['CounterpartyAccountDebtDynamics'],
    }),
    getContractSanctions: builder.query<
      ContractSanction[],
      ContractSanctionsParams
    >({
      query: ({ counterpartyId, contractId }) => ({
        url: `/${counterpartyId}/account/contracts/${contractId}/sanctions`,
      }),
      providesTags: () => ['ContractSanctions'],
    }),
  }),
});

export const useGetCounterpartyAccountDebtDynamicsInfiniteQuery =
  normalizeInfiniteQueryData(
    counterpartyAccountsApi.useGetCounterpartyAccountDebtDynamicsInfiniteQuery,
  );

export const {
  useGetCounterpartyAccountLedgerQuery,
  useGetCounterpartyAccrualsQuery,
  useGetCounterpartyAccrualDaysQuery,
  useGetContractSanctionsQuery,
} = counterpartyAccountsApi;
